import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import React, { Component } from "react";
import {
	ManagementContractContext,
	resetManagementContractContext,
	updateManagementContractContext
} from "@eres/ejari-core/contexts/ManagementContractContext";
import Strings from "@my-dld/assets/strings";
import FormSection from "@my-dld/components/Form/FormSection";
import FormAction from "@my-dld/components/Form/FormAction";
import FormRow from "@eres/forms-web/FormRow";
import { navigate } from "@my-dld/utils/StoreUtils";
import ListDataView from "@my-dld/components/DataView/ListDataView";
import { ImperialSystem, UnitSystem } from "@my-dld/utils/UnitUtils";
import { EjariProperty, EjariPropertyType } from "@eres/ejari-api/Entity";
import { LocaleUtil } from "@eres/core";
import { TenancyContactAction } from "@eres/ejari-core/constants";
import { EjariApi } from "@eres/ejari-api";
import { hideLoading, showErrorMessage, showLoading } from "@my-dld/utils/UIUtils";
import { MCContractDetails, OptionEJLookup } from "@eres/ejari-api/Entity/management_contract";
import moment from "moment";
import { EJLookupTransformer, normalizeAssociatedProperty } from "@eres/ejari-api/Utils";
import { ContractValidationError } from "@eres/ejari-api/Entity/tenant";

interface Props extends RoutedViewProps {
}

interface State {
}

class Properties extends Component<Props, State> {
	static contextType = ManagementContractContext;
	context!: React.ContextType<typeof ManagementContractContext>;

	constructor( props: Props ) {
		super( props );
		this.state = {};
	}

	componentDidMount(): void {
		if ( this.context.action === TenancyContactAction.RENEW_CONTRACT && this.context.id != null && this.context.managementContractDetails == null ) {
			this.getManagementContractDetails();
		}
	}

	render() {
		const { properties, action } = this.context;
		return <div className="tender-registration-form">
			<FormSection
				title={ `${ Strings.selected_properties } (${ properties?.length ?? 0 })` }
				{ ...( action === TenancyContactAction.NEW_CONTRACT ? {
					action: <button
						className={ "btn btn-grey btn_style1" } onClick={ this.onAddMoreProperties }>
						{ Strings.add_more_properties }
					</button>
				} : undefined ) }
			>
				{ properties && properties.length > 0 ? <PropertyTable
					type={ properties?.[ 0 ].PropertyTypeId }
					properties={ properties.map( p => ( { ...p, isSelected: true } ) ) }
					unit={ ImperialSystem }/> : null }
			</FormSection>
			<FormRow>
				<FormAction
					primaryActionLabel={ Strings.continue }
					onPrimaryAction={ this.onContinue }
					secondaryActionLabel={ Strings.cancel }
					onSecondaryAction={ this.onBack }/>
			</FormRow>
		</div>;
	}

	private getManagementContractDetails() {
		showLoading();
		EjariApi.getManagementContractDetails( this.context.id! )
		.then( contract => {
			hideLoading();
			const startDate = moment( contract.ContractEndDate ).add( 1, "day" ).toDate().asServerString();
			const contractDetail: Partial<MCContractDetails> = {
				AgreementCompany: EJLookupTransformer( contract.AgreementCompany as any ),
				ManagementContractTypeId: EJLookupTransformer( contract.ManagementContractType as any ),
				ManagementContractActivityId: EJLookupTransformer( contract.ManagementContractActivity as any ),
				ContractStartDate: startDate,
				IsActivityAccepted: contract.IsActivityAccepted,
			};
			const responsibilities: OptionEJLookup[] = contract.Responsibilities.map( r => {
				return {
					Identity: r.ResponsibilityID,
					NameEn: r.DescriptionEn,
					NameAr: r.DescriptionAr,
					isOwner: !r.IsCompany
				} as OptionEJLookup;
			} );
			updateManagementContractContext( {
				...this.context,
				managementContractDetails: contract,
				contractDetail: contractDetail as MCContractDetails,
				properties: contract.AssociatedDetailedProperties.map( normalizeAssociatedProperty ) as EjariProperty[],
				responsibilities: responsibilities,
				additionalTerms: contract.AdditionalTerms.filter(term => term.ArabicTerm && term.EnglishTerm),
			} );
		} )
		.catch( e => {
			hideLoading();
			showErrorMessage( e );
		} );
	}

	private onAddMoreProperties = () => {
		navigate( "properties/add" );
	};

	private onContinue = () => {
		navigate( "contract" );
	};

	private onBack = () => {
		resetManagementContractContext();
		this.props.history.go( -2 );
	};
}

export default Properties;

interface XProperty extends EjariProperty {
	isSelected?: boolean;
	index?: number;
	hasError?: ContractValidationError;
}

interface PropertyTableProps {
	properties?: XProperty[];
	type?: EjariPropertyType;
	unit: UnitSystem;
	onClick?: ( p: EjariProperty ) => void;
}

interface RowProps {
	property: XProperty;
	isSqFt: boolean;
	onClick?: ( p: EjariProperty ) => void;
}

function VillaHeader() {
	return <>
		<th>{ Strings.villa_name }</th>
		<th>{ Strings.area_label }</th>
		<th></th>
	</>;
}

function UnitHeader() {
	return <>
		<th>{ Strings.unit_number }</th>
		<th>{ Strings.building_name }</th>
		<th>{ Strings.area_label }</th>
		<th></th>
	</>;
}

function Row( props: RowProps ) {
	const { property, onClick } = props;
	const isUnit = property.PropertyTypeId === EjariPropertyType.UNIT;
	const unitNo = property.UnitNo;
	const value = LocaleUtil.getLocalValue( { en: property.BuildingNameEn, ar: property.BuildingNameAr } );
	const area = LocaleUtil.getLocalValue( { en: property.AreaEn, ar: property.AreaAr } );
	const hasError = property.hasError != null;
	const selectedTR = property.isSelected && !hasError ? "no-error" : "has-error";
	const isSelected = hasError ? "far fa-times-circle" : property.isSelected ? "far fa-check-circle" : "far fa-check-circle";
	const disabled = !onClick;
	const buttonStyle = {
		...disabled ? { border: "none" } : undefined,
		...hasError ? {} : undefined,
	};
	const iconErrorStyle = { opacity: 1, color: "#f00" };
	return <>
		<tr className={ selectedTR }>
			{ isUnit ? <td className="prop-unit-no">{ unitNo }</td> : null }
			<td
				className={ "prop-b-name" }>{ value }</td>
			<td className="prop-area">{ area }</td>
			<td className="td-action">
				<button
					disabled={ disabled }
					className="btn btn-grey btn-sm" onClick={ () => {
					if ( onClick ) onClick?.( property );
				} }>
					<i className={ isSelected }/>
				</button>
			</td>
		</tr>
		{ hasError ? <tr style={ hasError ? { backgroundColor: "#f000001c" } : undefined }>
			<td colSpan={ 4 }>{ LocaleUtil.getLocalValue( { en: property.hasError?.ErrorDescEn, ar: property.hasError?.ErrorDescAr } ) }</td>
		</tr> : null }
	</>;
}

export function PropertyTable( props: PropertyTableProps ) {
	const { properties, type = EjariPropertyType.VILLA, onClick } = props;
	if ( properties == null || properties.length < 1 ) {
		return null;
	}
	let Header = <VillaHeader/>;
	switch ( type ) {
		case EjariPropertyType.VILLA:
			break;
		case EjariPropertyType.UNIT:
			Header = <UnitHeader/>;
			break;
	}
	return <ListDataView
		id={ "properties" }
		items={ properties ? properties : [] }
		renderListHeader={ () => Header }
		renderListItem={ ( value: XProperty ) => {
			return <Row
				key={ value.PropertyId }
				isSqFt={ props.unit.Id === ImperialSystem.Id }
				property={ value }
				onClick={ onClick }/>;
		} }/>;
}
